import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { createReducer, on } from '@ngrx/store';

import {
    loadPlayerStatisticsData,
    playerStatisticsDataFailure,
    playerStatisticsDataSuccess,
    resetPlayerStatisticsState,
    setPlayerStatisticsOpenAndTrackingStatus,
    setTeamStatisticsOpenAndTrackingStatus,
} from './player-statistics.actions';
import { mergeStatisticsData } from './player-statistics.model';
import { playerStatisticsInitialState } from './player-statistics.state';

export const playerStatisticsReducer = createReducer(
    playerStatisticsInitialState,
    on(loadPlayerStatisticsData, (state, payload) => {
        const { eventId } = payload;
        const key = 'mainMarket' in payload ? 'mainMarket' : payload.marketFilter;
        const existingStats = state[eventId]?.[key];

        if (!existingStats) {
            return {
                ...state,
                [eventId]: {
                    ...state[eventId],
                    [key]: {
                        isLoading: true,
                        data: { isMainMarketOpen: true },
                        isError: false,
                    },
                },
            };
        }
        return state;
    }),
    on(playerStatisticsDataSuccess, (state, { eventId, marketFilter, data, isLoading, isError }) => {
        const existingStats = state[eventId]?.[marketFilter ?? 'mainMarket']?.data;
        const mergedData = mergeStatisticsData(data, existingStats, marketFilter);

        return {
            ...state,
            [eventId]: {
                ...state[eventId],
                [marketFilter ?? 'mainMarket']: { data: mergedData, isLoading, isError },
            },
        };
    }),
    on(playerStatisticsDataFailure, (state, { eventId, marketFilter, data, isLoading, isError }) => {
        const existingStats = state[eventId]?.[marketFilter ?? 'mainMarket']?.data;
        const mergedData = mergeStatisticsData(data, existingStats, marketFilter);
        return {
            ...state,
            [eventId]: {
                ...state[eventId],
                [marketFilter ?? 'mainMarket']: { data: mergedData, isLoading, isError },
            },
        };
    }),
    on(resetPlayerStatisticsState, (state) => playerStatisticsInitialState),
    on(
        setPlayerStatisticsOpenAndTrackingStatus,
        (state, { eventId, marketFilter, playerName, marketId, marketType, optionId, isOpen, tracking, loadedTracking }) => {
            const storeData = state[eventId]?.[marketFilter];
            if (!storeData) {
                return state;
            }
            const playerStatistics = storeData.data?.playerStatistics ?? [];
            const matchPlayer = playerStatistics.find((player) => {
                if (
                    marketType &&
                    [OptionGroupType.PlayerProps, OptionGroupType.PlayerMilestone, OptionGroupType.PlayerMilestoneOverUnder].includes(marketType)
                ) {
                    return marketId === player.marketId;
                }
                return marketId === player.marketId && player.playerName?.trim() === playerName.trim();
            });
            if (!matchPlayer) {
                playerStatistics.push({
                    playerName,
                    marketId: marketId ?? '',
                    isOpen,
                    tracking,
                    loadedTracking,
                    outcomeId: optionId,
                    playerNotFound: true,
                });
            } else {
                matchPlayer.isOpen = isOpen === undefined ? matchPlayer.isOpen : isOpen;
                matchPlayer.tracking = tracking === undefined ? matchPlayer.tracking : tracking;
                matchPlayer.loadedTracking = loadedTracking === undefined ? matchPlayer.loadedTracking : loadedTracking;
            }
            const statisticsData = {
                ...storeData.data,
                playerStatistics,
            };
            return {
                ...state,
                [eventId]: {
                    ...state[eventId],
                    [marketFilter]: {
                        ...storeData,
                        data: statisticsData,
                    },
                },
            };
        },
    ),
    on(setTeamStatisticsOpenAndTrackingStatus, (state, { eventId, isMainMarketOpen, mainMarketTracking, mainMarketLoadedTracking }) => {
        const storeData = state[eventId]?.['mainMarket'];
        if (!storeData) return state;
        const updatedData = {
            ...(storeData.data ?? {}),
            isMainMarketOpen,
            mainMarketTracking,
            mainMarketLoadedTracking,
        };
        return {
            ...state,
            [eventId]: {
                ...state[eventId],
                ['mainMarket']: {
                    ...storeData,
                    data: updatedData,
                },
            },
        };
    }),
);
